import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FullCalendarModule } from '@fullcalendar/angular';
import { CalendarOptions } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { FlatpickrDefaults } from 'angularx-flatpickr';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-daily-pricing-calendar',
  standalone: true,
  imports: [CommonModule, FullCalendarModule],
  providers: [FlatpickrDefaults],
  template: `
    <div>
      <!-- Pricing Overview - HIDDEN per user request -->
      @if (false && pricingStats.totalDates > 0) {
        <div class="bg-white dark:bg-white/[0.03] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 mb-6 overflow-hidden">
          <!-- Header -->
          <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">Pricing Overview</h3>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {{ pricingStats.totalDates }} {{ pricingStats.totalDates === 1 ? 'day' : 'days' }} configured
            </p>
          </div>

          <!-- Price Summary Cards -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 p-6 bg-gray-50 dark:bg-white/[0.02]">
            <!-- Min Price -->
            <div class="bg-white dark:bg-white/[0.05] rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Minimum</div>
              <div class="text-2xl font-bold text-blue-600 dark:text-blue-400">£{{ pricingStats.priceRange.min | number:'1.2-2' }}</div>
            </div>

            <!-- Average Price -->
            <div class="bg-white dark:bg-white/[0.05] rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Average</div>
              <div class="text-2xl font-bold text-green-600 dark:text-green-400">£{{ pricingStats.priceRange.avg | number:'1.2-2' }}</div>
            </div>

            <!-- Max Price -->
            <div class="bg-white dark:bg-white/[0.05] rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <div class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Maximum</div>
              <div class="text-2xl font-bold text-purple-600 dark:text-purple-400">£{{ pricingStats.priceRange.max | number:'1.2-2' }}</div>
            </div>
          </div>

          <!-- Annual Timeline Chart -->
          <div class="px-6 pb-6">
            <div class="bg-white dark:bg-white/[0.05] rounded-lg p-6 border border-gray-200 dark:border-gray-700">
              <div class="flex items-center justify-between mb-6">
                <h4 class="text-sm font-semibold text-gray-700 dark:text-gray-300">Annual Price Timeline</h4>
                <span class="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                  {{ pricingStats.dateRanges.length }} {{ pricingStats.dateRanges.length === 1 ? 'period' : 'periods' }}
                </span>
              </div>

              <!-- Y-axis and Chart -->
              <div class="flex gap-4">
                <!-- Y-axis labels -->
                <div class="flex flex-col justify-between text-xs text-gray-500 dark:text-gray-400 text-right" style="height: 280px; width: 60px;">
                  @if (pricingStats.priceRange.max !== pricingStats.priceRange.min) {
                    <span>£{{ pricingStats.priceRange.max | number:'1.2-2' }}</span>
                    <span>£{{ ((pricingStats.priceRange.max + pricingStats.priceRange.min) / 2) | number:'1.2-2' }}</span>
                    <span>£{{ pricingStats.priceRange.min | number:'1.2-2' }}</span>
                  } @else {
                    <span>&nbsp;</span>
                    <span class="font-semibold text-indigo-600 dark:text-indigo-400">£{{ pricingStats.priceRange.max | number:'1.2-2' }}</span>
                    <span>&nbsp;</span>
                  }
                </div>

                <!-- Timeline chart area -->
                <div class="flex-1 relative" style="height: 280px;">
                  <!-- Grid lines -->
                  <div class="absolute inset-0 flex flex-col justify-between pointer-events-none">
                    <div class="h-px bg-gray-200 dark:bg-gray-700"></div>
                    <div class="h-px bg-gray-200 dark:bg-gray-700"></div>
                    <div class="h-px bg-gray-200 dark:bg-gray-700"></div>
                  </div>

                  <!-- Month labels background (X-axis) -->
                  <div class="absolute bottom-0 left-0 right-0 h-8 flex border-t border-gray-200 dark:border-gray-700">
                    @for (month of displayMonths; track $index) {
                      <div class="flex-1 flex items-center justify-center text-[10px] text-gray-500 dark:text-gray-400 border-r border-gray-100 dark:border-gray-800">
                        {{ month }}
                      </div>
                    }
                  </div>

                  <!-- Price period bars -->
                  <div class="absolute inset-0 pb-8">
                    @for (range of pricingStats.dateRanges; track range.start) {
                      <div
                        class="absolute bottom-8 group cursor-pointer"
                        [style.left.%]="getYearPosition(range.start)"
                        [style.width.%]="getDateRangeWidth(range.start, range.end)"
                        [style.height.%]="getTimelineBarHeight(range.price)"
                        (click)="handleBarClick(range)">

                        <div class="h-full w-full rounded-t transition-all hover:opacity-80 {{ getTimelineBarColor(range.price) }}"></div>

                        <!-- Tooltip -->
                        <div class="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 hidden group-hover:block z-20 pointer-events-none">
                          <div class="bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded py-2 px-3 whitespace-nowrap shadow-lg">
                            <div class="font-semibold">£{{ range.price | number:'1.2-2' }}</div>
                            <div class="text-gray-300 dark:text-gray-600">
                              @if (range.start === range.end) {
                                {{ range.start | date:'dd MMM yyyy' }}
                              } @else {
                                {{ range.start | date:'dd MMM' }} - {{ range.end | date:'dd MMM yyyy' }}
                              }
                            </div>
                            <div class="text-gray-300 dark:text-gray-600 text-[10px]">{{ range.count }} {{ range.count === 1 ? 'day' : 'days' }}</div>
                            <div class="absolute top-full left-1/2 transform -translate-x-1/2">
                              <div class="border-4 border-transparent border-t-gray-900 dark:border-t-gray-100"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                </div>
              </div>

              <!-- Legend -->
              <div class="flex items-center justify-center flex-wrap gap-3 mt-6 text-xs text-gray-600 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-4">
                @for (price of getUniquePrices(); track price; let i = $index) {
                  <div class="flex items-center gap-1.5">
                    <div class="w-3 h-3 rounded {{ getTimelineBarColor(price) }}"></div>
                    <span class="font-medium">£{{ price | number:'1.2-2' }}</span>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      }

      <!-- Calendar View -->
      <div class="bg-white dark:bg-white/[0.03] rounded-lg shadow-sm p-4 border border-gray-200 dark:border-gray-800">
        <full-calendar [options]="calendarOptions"></full-calendar>
      </div>
    </div>
  `,
  styles: [`
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: rgba(156, 163, 175, 0.3);
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: rgba(156, 163, 175, 0.5);
    }
  `]
})
export class DailyPricingCalendarComponent implements OnInit {
  @Input() getDailyPrices!: () => Observable<any>;
  @Output() onPriceClick = new EventEmitter<{ startDate: Date; endDate: Date; price: number | null }>();

  pricingStats = {
    totalDates: 0,
    dateRanges: [] as { start: string; end: string; count: number; price: number }[],
    months: [] as { month: string; count: number; minPrice: number; maxPrice: number; avgPrice: number }[],
    priceRange: { min: 0, max: 0, avg: 0 }
  };

  // Timeline data - grouped by different resolutions
  timelineData: { date: Date; price: number; dateStr: string }[] = [];
  monthlyData: { month: string; avgPrice: number; minPrice: number; maxPrice: number; count: number }[] = [];
  weeklyData: { weekStart: Date; weekEnd: Date; avgPrice: number; minPrice: number; maxPrice: number; count: number; weekLabel: string }[] = [];

  // Year info for timeline
  currentYear: number = new Date().getFullYear();
  currentMonth: number = new Date().getMonth();
  displayMonths: string[] = [];

  calendarOptions: CalendarOptions = {
    plugins: [dayGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    weekends: true,
    firstDay: 0,
    locale: 'en-gb',
    timeZone: 'UTC',
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,dayGridWeek',
    },
    dayMaxEvents: false,
    events: [],
    eventContent: this.renderEventContent.bind(this),
    displayEventTime: false,
    eventDisplay: 'block',
    eventClick: this.handleEventClick.bind(this),
    dateClick: this.handleDateClick.bind(this),
  };

  ngOnInit(): void {
    this.setupDisplayMonths();
    this.loadPrices();
  }

  setupDisplayMonths(): void {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    this.displayMonths = [];
    for (let i = 0; i < 12; i++) {
      const monthIndex = (this.currentMonth + i) % 12;
      this.displayMonths.push(monthNames[monthIndex]);
    }
  }

  loadPrices(): void {
    this.getDailyPrices().subscribe({
      next: (response) => {
        const prices = response.data;
        this.calculatePricingStats(prices);
        this.prepareChartData(prices);

        const events = prices.map((priceData: any) => ({
          title: ``,
          start: priceData.date,
          allDay: true,
          classNames: ['price-event'],
          extendedProps: {
            price: priceData.price,
          },
        }));

        this.calendarOptions = {
          ...this.calendarOptions,
          events,
        };
      }
    });
  }

  prepareChartData(prices: any[]): void {
    const sortedPrices = [...prices].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Daily data
    this.timelineData = sortedPrices.map(p => ({
      date: new Date(p.date),
      price: parseFloat(p.price),
      dateStr: new Date(p.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    }));

    // Monthly aggregation
    const monthlyMap = new Map<string, { prices: number[], count: number }>();
    sortedPrices.forEach(p => {
      const date = new Date(p.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyMap.has(monthKey)) {
        monthlyMap.set(monthKey, { prices: [], count: 0 });
      }
      const data = monthlyMap.get(monthKey)!;
      data.prices.push(parseFloat(p.price));
      data.count++;
    });

    this.monthlyData = Array.from(monthlyMap.entries()).map(([key, data]) => {
      const [year, month] = key.split('-');
      const monthDate = new Date(parseInt(year), parseInt(month) - 1, 1);
      return {
        month: monthDate.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
        avgPrice: data.prices.reduce((sum, p) => sum + p, 0) / data.prices.length,
        minPrice: Math.min(...data.prices),
        maxPrice: Math.max(...data.prices),
        count: data.count
      };
    });

    // Weekly aggregation
    const weeklyMap = new Map<string, { prices: number[], dates: Date[], count: number }>();
    sortedPrices.forEach(p => {
      const date = new Date(p.date);
      const weekStart = new Date(date);
      weekStart.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
      const weekKey = weekStart.toISOString().split('T')[0];

      if (!weeklyMap.has(weekKey)) {
        weeklyMap.set(weekKey, { prices: [], dates: [], count: 0 });
      }
      const data = weeklyMap.get(weekKey)!;
      data.prices.push(parseFloat(p.price));
      data.dates.push(date);
      data.count++;
    });

    this.weeklyData = Array.from(weeklyMap.entries()).map(([key, data]) => {
      const weekStart = new Date(key);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      return {
        weekStart,
        weekEnd,
        avgPrice: data.prices.reduce((sum, p) => sum + p, 0) / data.prices.length,
        minPrice: Math.min(...data.prices),
        maxPrice: Math.max(...data.prices),
        count: data.count,
        weekLabel: `${weekStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} - ${weekEnd.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
      };
    });
  }

  getTimelineBarHeight(price: number): number {
    if (!this.pricingStats.priceRange.max || this.pricingStats.priceRange.max === 0) return 50;
    const range = this.pricingStats.priceRange.max - this.pricingStats.priceRange.min;
    // If all prices are the same, show bars at 60% height
    if (range === 0) return 60;
    // Calculate relative height (20% minimum to ensure visibility)
    const relativeHeight = ((price - this.pricingStats.priceRange.min) / range) * 80;
    return Math.max(20, relativeHeight);
  }

  getTimelineBarColor(price: number): string {
    // Extended array of distinct colors for each unique price
    const colors = [
      // Primary colors (400 shade)
      'bg-blue-400 dark:bg-blue-500/80',
      'bg-emerald-400 dark:bg-emerald-500/80',
      'bg-purple-400 dark:bg-purple-500/80',
      'bg-amber-400 dark:bg-amber-500/80',
      'bg-rose-400 dark:bg-rose-500/80',
      'bg-cyan-400 dark:bg-cyan-500/80',
      'bg-orange-400 dark:bg-orange-500/80',
      'bg-indigo-400 dark:bg-indigo-500/80',
      'bg-pink-400 dark:bg-pink-500/80',
      'bg-teal-400 dark:bg-teal-500/80',
      'bg-lime-400 dark:bg-lime-500/80',
      'bg-fuchsia-400 dark:bg-fuchsia-500/80',

      // Secondary colors (500 shade)
      'bg-blue-500 dark:bg-blue-600/80',
      'bg-emerald-500 dark:bg-emerald-600/80',
      'bg-purple-500 dark:bg-purple-600/80',
      'bg-amber-500 dark:bg-amber-600/80',
      'bg-rose-500 dark:bg-rose-600/80',
      'bg-cyan-500 dark:bg-cyan-600/80',
      'bg-orange-500 dark:bg-orange-600/80',
      'bg-indigo-500 dark:bg-indigo-600/80',
      'bg-pink-500 dark:bg-pink-600/80',
      'bg-teal-500 dark:bg-teal-600/80',
      'bg-lime-500 dark:bg-lime-600/80',
      'bg-fuchsia-500 dark:bg-fuchsia-600/80',

      // Lighter colors (300 shade)
      'bg-blue-300 dark:bg-blue-400/80',
      'bg-emerald-300 dark:bg-emerald-400/80',
      'bg-purple-300 dark:bg-purple-400/80',
      'bg-amber-300 dark:bg-amber-400/80',
      'bg-rose-300 dark:bg-rose-400/80',
      'bg-cyan-300 dark:bg-cyan-400/80',
      'bg-orange-300 dark:bg-orange-400/80',
      'bg-indigo-300 dark:bg-indigo-400/80',
      'bg-pink-300 dark:bg-pink-400/80',
      'bg-teal-300 dark:bg-teal-400/80',
      'bg-lime-300 dark:bg-lime-400/80',
      'bg-fuchsia-300 dark:bg-fuchsia-400/80',
    ];

    // Get unique prices and find index
    const uniquePrices = this.getUniquePrices();
    const priceIndex = uniquePrices.indexOf(price);

    // Return color based on index, cycling through colors if needed
    return colors[priceIndex % colors.length];
  }

  getUniquePrices(): number[] {
    return Array.from(new Set(this.pricingStats.dateRanges.map(r => r.price))).sort((a, b) => a - b);
  }

  // Calculate position of date on 12-month rolling timeline (0-100%)
  getYearPosition(dateStr: string): number {
    const date = new Date(dateStr);
    const timelineStart = new Date(this.currentYear, this.currentMonth, 1);
    const daysSinceStart = Math.floor((date.getTime() - timelineStart.getTime()) / (1000 * 60 * 60 * 24));

    // If date is before timeline start, return negative (will be clipped)
    if (daysSinceStart < 0) return -10;

    const position = (daysSinceStart / 365) * 100;

    // Cap at 100% to prevent overflow
    return Math.min(position, 99.5);
  }

  // Calculate width of date range on 12-month rolling timeline (0-100%)
  getDateRangeWidth(startStr: string, endStr: string): number {
    const start = new Date(startStr);
    const end = new Date(endStr);
    const timelineStart = new Date(this.currentYear, this.currentMonth, 1);
    const timelineEnd = new Date(this.currentYear, this.currentMonth + 12, 1);

    // Clip start and end to timeline bounds
    const clippedStart = new Date(Math.max(start.getTime(), timelineStart.getTime()));
    const clippedEnd = new Date(Math.min(end.getTime(), timelineEnd.getTime()));

    // If range is completely outside timeline, return 0
    if (clippedStart >= timelineEnd || clippedEnd <= timelineStart) return 0;

    const daysInRange = Math.floor((clippedEnd.getTime() - clippedStart.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const width = (daysInRange / 365) * 100;

    // Minimum 0.5% width for visibility, but don't exceed remaining space
    const position = this.getYearPosition(startStr);
    const maxWidth = Math.max(0, 100 - position);

    return Math.min(Math.max(0.5, width), maxWidth);
  }

  isLeapYear(year: number): boolean {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  }

  calculatePricingStats(prices: any[]): void {
    this.pricingStats.totalDates = prices.length;

    if (prices.length === 0) {
      this.pricingStats.dateRanges = [];
      this.pricingStats.months = [];
      this.pricingStats.priceRange = { min: 0, max: 0, avg: 0 };
      return;
    }

    const sortedPrices = [...prices].sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // Calculate overall price range
    const allPrices = prices.map(p => parseFloat(p.price));
    this.pricingStats.priceRange = {
      min: Math.min(...allPrices),
      max: Math.max(...allPrices),
      avg: allPrices.reduce((sum, price) => sum + price, 0) / allPrices.length
    };

    // Calculate date ranges with prices
    const ranges: { start: string; end: string; count: number; price: number }[] = [];
    let rangeStart = sortedPrices[0].date;
    let rangeEnd = sortedPrices[0].date;
    let rangePrice = parseFloat(sortedPrices[0].price);
    let count = 1;

    for (let i = 1; i < sortedPrices.length; i++) {
      const currentDate = new Date(sortedPrices[i].date);
      const prevDate = new Date(sortedPrices[i - 1].date);
      const dayDiff = (currentDate.getTime() - prevDate.getTime()) / (1000 * 60 * 60 * 24);
      const currentPrice = parseFloat(sortedPrices[i].price);

      if (dayDiff === 1 && currentPrice === rangePrice) {
        rangeEnd = sortedPrices[i].date;
        count++;
      } else {
        ranges.push({ start: rangeStart, end: rangeEnd, count, price: rangePrice });
        rangeStart = sortedPrices[i].date;
        rangeEnd = sortedPrices[i].date;
        rangePrice = currentPrice;
        count = 1;
      }
    }
    ranges.push({ start: rangeStart, end: rangeEnd, count, price: rangePrice });
    this.pricingStats.dateRanges = ranges;

    // Calculate months with price stats
    const monthData = new Map<string, number[]>();
    sortedPrices.forEach(priceData => {
      const date = new Date(priceData.date);
      const monthKey = date.toLocaleString('en-GB', { year: 'numeric', month: 'long' });
      const price = parseFloat(priceData.price);

      if (!monthData.has(monthKey)) {
        monthData.set(monthKey, []);
      }
      monthData.get(monthKey)!.push(price);
    });

    this.pricingStats.months = Array.from(monthData.entries())
      .map(([month, monthPrices]) => ({
        month,
        count: monthPrices.length,
        minPrice: Math.min(...monthPrices),
        maxPrice: Math.max(...monthPrices),
        avgPrice: monthPrices.reduce((sum, p) => sum + p, 0) / monthPrices.length
      }))
      .sort((a, b) => {
        const [aMonth, aYear] = a.month.split(' ');
        const [bMonth, bYear] = b.month.split(' ');
        const aDate = new Date(`${aMonth} 1, ${aYear}`);
        const bDate = new Date(`${bMonth} 1, ${bYear}`);
        return aDate.getTime() - bDate.getTime();
      });
  }

  renderEventContent(eventInfo: any): any {
    const { price } = eventInfo.event.extendedProps;
    return {
      html: `
      <div class="flex flex-col p-1 w-full h-full">
        <div class="text-xs">
          <div class="price-row">
            <span class="font-semibold">Price:</span>
            <span>&pound${price}</span>
          </div>
        </div>
      </div>
    `,
    };
  }

  refresh(): void {
    this.loadPrices();
  }

  handleEventClick(info: any): void {
    // Parse date string to avoid timezone issues
    const dateStr = info.event.startStr || info.event.start.toISOString().split('T')[0];
    const date = this.parseUTCDate(dateStr);
    const price = info.event.extendedProps.price;
    this.populateBulkUpdateForm(date, date, price);
  }

  handleDateClick(info: any): void {
    // User clicked on an empty date cell
    const dateStr = info.dateStr;
    const date = this.parseUTCDate(dateStr);
    // Pass null for price to indicate empty date
    this.populateBulkUpdateForm(date, date, null);
  }

  handleBarClick(range: any): void {
    // Parse date strings to avoid timezone issues
    const startDate = this.parseUTCDate(range.start);
    const endDate = this.parseUTCDate(range.end);
    this.populateBulkUpdateForm(startDate, endDate, range.price);
  }

  // Parse date string as UTC to avoid timezone shifts
  private parseUTCDate(dateStr: string): Date {
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  populateBulkUpdateForm(startDate: Date, endDate: Date, price: number | null): void {
    // Scroll to bulk update form
    const bulkUpdateElement = document.querySelector('app-daily-pricing-bulk-update');
    if (bulkUpdateElement) {
      bulkUpdateElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    // Emit event to parent to populate form
    this.onPriceClick.emit({ startDate, endDate, price });
  }
}
